import { COGNITO_REGION, USER_POOL_CLIENT_ID, USER_POOL_ID, DEMO_CLIENT_ID } from './config';
import type { Session } from './types';

const KEY = 'leadsirl.session';

function parseJwt(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    const claims = parseJwt(s.idToken);
    const exp = Number(claims.exp ?? 0) * 1000;
    if (Date.now() > exp - 30_000) {
      localStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveSession(s: Session) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession() {
  localStorage.removeItem(KEY);
}

export async function signIn(email: string, password: string): Promise<Session> {
  const url = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: USER_POOL_CLIENT_ID,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    }),
  });
  const data = await res.json();
  if (!res.ok || data.__type || !data.AuthenticationResult) {
    const msg = data.message || data.__type || 'Sign-in failed';
    throw new Error(String(msg).replace('NotAuthorizedException:', '').trim());
  }
  const idToken = data.AuthenticationResult.IdToken as string;
  const accessToken = data.AuthenticationResult.AccessToken as string;
  const claims = parseJwt(idToken);
  const groups = (claims['cognito:groups'] as string[]) ?? [];
  const clientId = (claims['custom:clientId'] as string) || DEMO_CLIENT_ID;
  const session: Session = {
    idToken,
    accessToken,
    email: String(claims.email ?? email),
    groups,
    clientId,
  };
  saveSession(session);
  void USER_POOL_ID;
  return session;
}
